import { createServiceTrackedHeaders } from './createServiceTrackedHeaders';
import { uuid } from '../shared/utils';
import { getApiConfig } from '../services/api/utils';
import { DeliverySystemApiClient } from '@highereducation/delivery-system';

// Checks whether a given string is empty or consists entirely of whitespace characters
const isEmptyString = (str: string) => str.match(/^ *$/) !== null;

// Checks if a string contains numbers
const containNumbers = (str: string) => /\d/.test(str);

// Validates that the name is not empty and does not contain numbers
export const validateName = (name: string) => {
  if (isEmptyString(name) || containNumbers(name)) {
    throw new Error('Name is empty or contains numbers');
  }
  return true;
};

const getDeliverySystemClient = () => {
  const config = getApiConfig();
  const baseUrl = config.deliverySystemApiUrl ?? '';
  const headers = createServiceTrackedHeaders(uuid());

  return new DeliverySystemApiClient({
    baseUrl,
    defaultHeaders: headers,
  });
};

// LDS API call that validates a phone number string
/**
 *
 * @param phoneNumber user's phone number
 * @returns true if the provided phone number is valid
 */
export const validatePhone = async (phoneNumber: string) => {
  try {
    const client = getDeliverySystemClient();
    const result = await client.validatePhone({ phoneNumber });
    return result;
  } catch (error) {
    console.error('Could not validate phone', error);
    return { validPhoneNumber: false, error: error };
  }
};

export type ValidateZip = (zip: string) => Promise<
  | {
      city: string;
      zipcode: string;
      state: string;
      stateAbbr: string;
      country: string;
      validZip?: boolean;
    }
  | Error
>;

export const validateZip = async (zip: string) => {
  try {
    const client = getDeliverySystemClient();
    const result = await client.validateZip({ zip });

    if ('validZip' in result && result.validZip === false) {
      return { validZip: false as const, error: result };
    }

    if ('stateAbbr' in result) {
      return { validZip: true as const, ...result };
    }

    return { validZip: false as const, error: 'Invalid response format' };
  } catch (error) {
    console.error('Could not validate zip', error);
    return { validZip: false, error: error };
  }
};

// Validates if a string is in a valid email format
export const validateEmail = (email: string) => {
  /* eslint-disable no-useless-escape */
  const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  return regex.test(email);
};
